import React, { FC, ReactElement, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@store/store'
import {
  fetchPlaylistById,
  selectIsPlaylistIdInProgress,
  selectPlaylistById,
} from '@store/slices/playlist.slice'
import { EntityId } from '@reduxjs/toolkit'
import {
  AISlotModel,
  fetchAiSlotById,
  selectAiSlotById,
  selectIsAiSlotIdInProgress,
} from '@store/slices/aiSlot.slice'
import { Accordion } from 'react-bootstrap'
import { toHourMinString } from '@shared/tools/time.tools'
import {
  fetchAllRegions,
  selectAllRegions,
  selectRegionById,
  selectRegionLoading,
} from '@store/slices/region.slice'
import {
  fetchAllNarrations,
  NarrationModel,
  selectNarrationLoading,
  selectNarrationsByIds,
} from '@store/slices/narration.slice'
import { DjangoFilter } from '@store/slices/generic.slice'

const PlaylistPublishedComponent: FC = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { playlistId } = useParams()
  const playlist = useSelector((state: RootState) =>
    playlistId ? selectPlaylistById(state, playlistId) : null
  )
  const playlistLoading = useSelector((state: RootState) =>
    playlistId ? selectIsPlaylistIdInProgress(state, playlistId) : playlistId
  )

  useEffect(() => {
    if (playlistId && !playlistLoading && !playlist) {
      dispatch(fetchPlaylistById(playlistId))
    }
  }, [])

  // Playlist component details here
  return (
    <div className={'my-2 overflow-scroll'} style={{ maxHeight: '800px' }}>
      {playlist &&
        playlist.aiSlots &&
        playlist.aiSlots.map((slotId: EntityId) => (
          <SlotPublished key={slotId} slotId={slotId}></SlotPublished>
        ))}
    </div>
  )
}

export default PlaylistPublishedComponent

const SlotPublished: FC<{ slotId: EntityId }> = ({ slotId }) => {
  const dispatch = useDispatch<AppDispatch>()
  const aiSlot: AISlotModel | undefined = useSelector((state: RootState) =>
    selectAiSlotById(state, slotId)
  )
  const slotLoading = useSelector((state: RootState) =>
    selectIsAiSlotIdInProgress(state, slotId)
  )
  const narrationsLoading = useSelector(selectNarrationLoading)
  const slotNarrations: NarrationModel[] = useSelector((state: RootState) =>
    aiSlot ? selectNarrationsByIds(state, aiSlot.narrations) : []
  )

  useEffect(() => {
    if (!aiSlot && !slotLoading) {
      dispatch(fetchAiSlotById(slotId))
    }
  }, [])

  useEffect(() => {
    if (
      aiSlot?.narrations &&
      aiSlot?.narrations.length != 0 &&
      (!slotNarrations || slotNarrations.length == 0) &&
      !narrationsLoading
    ) {
      fetchNarrationsForSlot()
    }
  }, [slotNarrations, aiSlot?.narrations])

  const fetchNarrationsForSlot = () => {
    const filter = new DjangoFilter<AISlotModel>()
    aiSlot.narrations && filter.addIdFilter(aiSlot.narrations)
    dispatch(fetchAllNarrations(filter))
  }

  const getStatusString = (): ReactElement => {
    const published = slotNarrations
      ? slotNarrations.reduce((accumulator: number, item: NarrationModel) => {
          if (item.uploaded) {
            return accumulator + 1
          } else {
            return accumulator
          }
        }, 0)
      : 0
    const total = aiSlot && aiSlot.narrations ? aiSlot.narrations.length : 0
    return (
      <div>
        <span>
          Publikováno <strong>{published}</strong>/<strong>{total}</strong>
        </span>
      </div>
    )
  }
  return (
    <div className="my-2">
      <Accordion defaultActiveKey="0">
        <Accordion.Item eventKey="0">
          <Accordion.Header>
            <div className="d-flex gap-2 align-items-center">
              <h4 className={'p-0 m-0'}>
                {aiSlot && toHourMinString(aiSlot.exactTime)}
              </h4>
              <span>{getStatusString()}</span>
            </div>
          </Accordion.Header>
          <Accordion.Body>
            {aiSlot &&
              slotNarrations &&
              slotNarrations.map((narration) => (
                <NarrationPublished
                  key={aiSlot.id.toString() + narration.region.toString()}
                  narration={narration}
                  slotId={slotId}
                />
              ))}
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    </div>
  )
}

const NarrationPublished: FC<{
  narration: NarrationModel
  slotId: EntityId
}> = ({ narration, slotId }) => {
  const { playlistId } = useParams()
  const regionsLoading = useSelector(selectRegionLoading)
  const dispatch = useDispatch<AppDispatch>()
  const regions = useSelector(selectAllRegions)
  const region = useSelector((state: RootState) =>
    narration.region ? selectRegionById(state, narration.region) : null
  )
  useEffect(() => {
    if (regions?.length == 0 && !regionsLoading) dispatch(fetchAllRegions())
  }, [regions])
  const isNarrationUpdated =
    narration &&
    narration.audioCreated &&
    (!narration.uploaded ||
      (narration.uploaded &&
        new Date(narration.uploaded) < new Date(narration.audioCreated)))
  return (
    <div className="d-flex gap-2 justify-content-between">
      <div>
        <Link
          to={`/playlist/${playlistId}/slot/${slotId}/region/${narration.region}`}
        >
          {region?.name}
        </Link>
      </div>
      <div className={'w-75 d-flex gap-2'}>
        {narration.uploaded ? (
          <>
            <strong className={'text-success'}>
              Publikace proběhla v {toHourMinString(narration.uploaded)}
            </strong>
            {isNarrationUpdated && (
              <strong className={'text-primary'}>
                Aktualizace proběhla v {toHourMinString(narration.uploaded)}
              </strong>
            )}
          </>
        ) : (
          <strong>Region ještě nění publikován</strong>
        )}
      </div>
    </div>
  )
}
